import { useListVendorItemPricingHistory } from "@workspace/api-client-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Skeleton } from "@/components/ui/skeleton";
import { History } from "lucide-react";

interface Props {
  itemId: number;
  itemName: string;
}

/**
 * Phase 2.1 D-23: read-only price history sub-section (LIVE data binding).
 *
 * Renders the latest 10 rows from vendor_item_pricing_history for this item.
 * No edit affordance — historical fact is fact. Backed by
 * GET /inventory/pricing/history?itemId={itemId} (Plan 08 Sub-task 3a).
 *
 * Schema note: vendor_item_pricing_history is keyed by pricing_id (NOT
 * item_id directly) and stores oldPrice / newPrice / changeDate / reason
 * / changedById. The endpoint resolves pricing_ids for this item before
 * filtering history rows. Columns rendered match the actual schema.
 */
export function ItemsPriceHistoryRow({ itemId, itemName }: Props) {
  const { data: rows, isLoading } = useListVendorItemPricingHistory({ itemId });
  const top10 = (rows ?? []).slice(0, 10);

  return (
    <Card className="bg-muted/30">
      <CardHeader>
        <CardTitle className="flex items-center text-sm font-semibold">
          <History className="mr-2 h-4 w-4" />Price History — {itemName}
        </CardTitle>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <Skeleton className="h-24 w-full" />
        ) : top10.length === 0 ? (
          <p className="text-xs text-muted-foreground">
            No price changes recorded yet. Stock_In commits append a row each
            time per-vendor pricing changes.
          </p>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="text-xs">Date</TableHead>
                <TableHead className="text-xs">Pricing #</TableHead>
                <TableHead className="text-xs text-right">Old Price</TableHead>
                <TableHead className="text-xs text-right">New Price</TableHead>
                <TableHead className="text-xs">Reason</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {top10.map((row) => {
                const date = row.changeDate
                  ? new Date(row.changeDate).toLocaleDateString()
                  : "—";
                return (
                  <TableRow key={row.id}>
                    <TableCell className="text-xs">{date}</TableCell>
                    <TableCell className="font-mono text-xs">
                      #{row.pricingId}
                    </TableCell>
                    <TableCell className="text-xs text-right font-mono tabular-nums">
                      {Number(row.oldPrice).toFixed(2)}
                    </TableCell>
                    <TableCell className="text-xs text-right font-mono tabular-nums">
                      {Number(row.newPrice).toFixed(2)}
                    </TableCell>
                    <TableCell className="text-xs">
                      {row.reason ?? "—"}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}
      </CardContent>
    </Card>
  );
}
